import { Injectable } from '@angular/core';
import { datadogLogs } from '@datadog/browser-logs';
import { FeatureFlagFacade } from 'src/app/core/state/facades/feature-flag.facade';
import { environment } from 'src/environments/environment';
import { VERSION } from 'src/environments/version';

/**
 * This service send logs to Datadog from web browser with the Datadog browser logs SDK.
 */
@Injectable({
  providedIn: 'root',
})
export class LoggerService {
  initialised = false;

  readonly DATADOG_BROWSER_LOGS_CONFIG_DEFAULT = {
    env: environment.environmentName, // The application’s environment.
    version: VERSION.version, // The application’s version.
    forwardErrorsToLogs: true, // Forward console.error logs, uncaught exceptions and network errors to Datadog.
    forwardConsoleLogs: 'all', // Forward logs from console.* to Datadog.
    forwardReports: 'all', // Forward reports from the Reporting API to Datadog.
    sessionSampleRate: 100, // Percentage of sessions to track.
    telemetrySampleRate: 100, // Telemetry data sample rate about SDK execution sent to Datadog.
  } as const;

  constructor(private featureFlagFacade: FeatureFlagFacade) {}

  /**
   * Initialise logger service.
   * This method should be called once on application startup.
   */
  async initialise(): Promise<void> {
    // Check if datadogConfig is present in the current environment object.
    const propertyDescriptor = Object.getOwnPropertyDescriptor(environment, 'datadogConfig');

    if (!this.initialised && propertyDescriptor) {
      const datadogConfig = propertyDescriptor.value;

      /**
       * FEATURE FLAG IMPLEMENTATION:
       * KEY: 'core__datadog_browser_logs'
       * DESCRIPTION: Controls the Datadog Browser Log feature for web frontend.
       */
      const { core__datadog_browser_logs: isDatadogBrowserLogsEnabled } = await this.featureFlagFacade.isFeatureEnabled(
        ['core__datadog_browser_logs'],
      );

      if (isDatadogBrowserLogsEnabled) {
        datadogLogs.init({
          ...this.DATADOG_BROWSER_LOGS_CONFIG_DEFAULT,
          ...datadogConfig.common,
          ...datadogConfig.browserLogs,
        });
        this.initialised = true;
      }
    }
  }
}
